import { BASE_AUTH_OFFICE_API, BASE_KIOSK_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import {
  MachineModelResponse,
  MachineDefaultCreditResponse,
  KioskDetailsResponse,
  KioskLocationsResponse,
  SparkAssignableLocation,
  VpostMappingResponse,
  VpostMappingRequest,
  RemoteStartMappingData,
} from '../types/assign-kiosk-types';

/** Raw HTTP client for the assign-kiosk feature. */
export class AssignKioskApi {
  constructor(
    private readonly officeBaseUrl: string = BASE_AUTH_OFFICE_API,
    private readonly kioskBaseUrl: string = BASE_KIOSK_API,
    private readonly tokenManager: TokenManager
  ) {}

  /**
   * API #1 — GET /api/v1/actor/machines/:machineId/machine-model
   * Returns the machine model string (e.g. "EV_KIOSK").
   */
  async getMachineModel(machineId: string): Promise<MachineModelResponse> {
    const endpoint = `/actor/machines/${machineId}/machine-model`;
    return this.officeRequest<MachineModelResponse>(endpoint);
  }

  /**
   * GET /api/v1/actor/machines/:machineId/default-credit
   * Live Lynx Default Credit (attr 584) with decimal place (523) and currency from the machine's Lynx CurrencyID.
   * Failures are non-fatal for the caller, so error toasts are suppressed.
   */
  async getMachineDefaultCredit(machineId: string): Promise<MachineDefaultCreditResponse> {
    const endpoint = `/actor/machines/${machineId}/default-credit`;
    return this.officeRequest<MachineDefaultCreditResponse>(endpoint, [400, 403, 404, 500, 502, 503]);
  }

  /**
   * API #2 — GET /kiosk/v1/admin/kiosk?machineId=:machineId
   * Returns the current kiosk assignment details.
   */
  async getKioskDetails(machineId: string): Promise<KioskDetailsResponse> {
    const endpoint = `/admin/kiosk?machineId=${machineId}`;
    // 404 = "kiosk not found"
    return this.kioskRequest<KioskDetailsResponse>(endpoint, [404]);
  }

  /**
   * API #3a — GET /kiosk/v1/admin/locations?operatorId=:operatorId[&locationId=:locationId]
   * When locationId is provided, the backend returns only that location.
   */
  async getLocationsByOperatorId(operatorId: string, locationId?: string): Promise<KioskLocationsResponse> {
    const params = new URLSearchParams({ operatorId });
    if (locationId) params.set('locationId', locationId);
    return this.kioskRequest<KioskLocationsResponse>(`/admin/locations?${params.toString()}`);
  }

  /**
   * API #3b — GET /kiosk/v1/admin/locations?machineId=:machineId
   * Fallback when operatorId is not available (matches Flutter's fallback).
   */
  async getLocationsByMachineId(machineId: string): Promise<KioskLocationsResponse> {
    const endpoint = `/admin/locations?machineId=${machineId}`;
    return this.kioskRequest<KioskLocationsResponse>(endpoint);
  }

  /**
   * API #4 — POST /kiosk/v1/admin/registerKiosk?machineId=:machineId&countryCode=:country&partyId=:party&locationId=:locationId
   * Assigns the kiosk to the specified location with connector settings.
   */
  async registerKiosk(
    machineId: string,
    countryCode: string,
    partyId: string,
    locationId: string,
    settingsJson: string
  ): Promise<void> {
    const params = new URLSearchParams({ machineId, countryCode, partyId, locationId });
    const endpoint = `/admin/registerKiosk?${params.toString()}`;
    const body = { settings: settingsJson };

    const headers = await this.tokenManager.getAuthHeaders();
    await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.kioskBaseUrl}${endpoint}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(body),
      },
      'AssignKiosk API',
      endpoint,
      [400, 401, 403, 404, 405, 422, 500]
    );
  }

  // ─── SPARK_DEVICE_START APIs ──────────────────────────────────────────────

  /**
   * SPARK #1 — GET .../ocpi-to-vpost/spark-devices/:machineId/assignable-locations-evses
   * Returns locations with assignable EVSEs for a SPARK device.
   */
  async getAssignableLocationsEvses(machineId: string): Promise<SparkAssignableLocation[]> {
    const endpoint = `/ocpi-to-vpost/spark-devices/${machineId}/assignable-locations-evses`;
    return this.officeRequest<SparkAssignableLocation[]>(endpoint);
  }

  /**
   * SPARK #2 — GET .../ocpi-to-vpost/spark-devices/:machineId/mapping/location-with-evses/with-labels
   * Returns the current SPARK device mapping (assigned location + EVSE settings).
   */
  async getSparkDeviceMapping(machineId: string): Promise<VpostMappingResponse | null> {
    const endpoint = `/ocpi-to-vpost/spark-devices/${machineId}/mapping/location-with-evses/with-labels`;
    return this.officeRequest<VpostMappingResponse | null>(endpoint, [400, 401, 403, 404, 405, 422]);
  }

  /**
   * SPARK #3 — PUT .../ocpi-to-vpost/spark-devices/:machineId/mapping/location-with-evses/with-labels
   * Saves the SPARK device mapping.
   */
  async putSparkDeviceMapping(machineId: string, body: VpostMappingRequest): Promise<void> {
    const endpoint = `/ocpi-to-vpost/spark-devices/${machineId}/mapping/location-with-evses/with-labels`;
    const headers = await this.tokenManager.getAuthHeaders();
    await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.officeBaseUrl}${endpoint}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(body),
      },
      'AssignKiosk API',
      endpoint,
      [404]
    );
  }

  // ─── SPARK_REMOTE_START APIs ──────────────────────────────────────────────

  /**
   * REMOTE #1 — GET .../ocpi-to-vpost/spark-devices/:machineId/mapping/location-with-evses
   * Returns the current remote-start mapping (location_id + evse_uids only, no labels).
   */
  async getRemoteStartMapping(machineId: string): Promise<RemoteStartMappingData | null> {
    const endpoint = `/ocpi-to-vpost/spark-devices/${machineId}/mapping/location-with-evses`;
    return this.officeRequest<RemoteStartMappingData | null>(endpoint, [400, 401, 403, 404, 405, 422]);
  }

  /**
   * REMOTE #2 — PUT .../ocpi-to-vpost/spark-devices/:machineId/mapping/location-with-evses
   * Saves the remote-start mapping.
   */
  async putRemoteStartMapping(machineId: string, body: RemoteStartMappingData): Promise<void> {
    const endpoint = `/ocpi-to-vpost/spark-devices/${machineId}/mapping/location-with-evses`;
    const headers = await this.tokenManager.getAuthHeaders();
    await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.officeBaseUrl}${endpoint}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(body),
      },
      'AssignKiosk API',
      endpoint,
      [404]
    );
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  private async officeRequest<T>(endpoint: string, suppressToastForStatus?: number[]): Promise<T> {
    const headers = await this.tokenManager.getAuthHeaders();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.officeBaseUrl}${endpoint}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json', ...headers } },
      'AssignKiosk API',
      endpoint,
      suppressToastForStatus
    );
    return response.json();
  }

  private async kioskRequest<T>(endpoint: string, suppressToastForStatus?: number[]): Promise<T> {
    const headers = await this.tokenManager.getAuthHeaders();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.kioskBaseUrl}${endpoint}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json', ...headers } },
      'AssignKiosk API',
      endpoint,
      suppressToastForStatus
    );
    return response.json();
  }
}
